/**
 * TierAdjustDialog - 管理员调整用户等级体系 v3
 * 独立对话框，不影响现有 UserEditDialog
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { getMyTier, getAllLevels, adjustUserTier, codeToLabel, tierFromCode } from '../../services/tierApi';

const TierAdjustDialog = ({ open, user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tier, setTier] = useState(null);
  const [allLevels, setAllLevels] = useState([]);
  const [targetCode, setTargetCode] = useState(11);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const [tierRes, levelsRes] = await Promise.all([
          getMyTier(user._id),
          allLevels.length === 0 ? getAllLevels() : Promise.resolve({ success: true, data: allLevels }),
        ]);
        if (cancel) return;
        if (tierRes?.success) {
          setTier(tierRes.data);
          setTargetCode(tierRes.data.current_code || 11);
        }
        if (levelsRes?.success) setAllLevels(levelsRes.data || []);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.message || e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, user]);

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await adjustUserTier(user._id, parseInt(targetCode, 10), reason || 'admin_manual_adjust');
      if (res?.success) {
        setSuccess(`等级已调整为 ${codeToLabel(parseInt(targetCode, 10))}`);
        if (onSuccess) onSuccess(res.data);
        setTimeout(() => {
          onClose();
          setSuccess(null);
        }, 800);
      } else {
        setError(res?.message || '调整失败');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const targetTier = tierFromCode(parseInt(targetCode, 10));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        调整用户等级 (LevelUp 2.0)
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {user?.username} ({user?.email})
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            {tier && (
              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">当前等级</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography sx={{ fontSize: 24 }}>{tierFromCode(tier.current_code).icon}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {tier.tier_name} · {codeToLabel(tier.current_code)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`本月 ${tier.monthly_points || 0}/${tier.monthly_required || 100}`} />
                  <Chip size="small" label={`累计 ${tier.total_points || 0}`} variant="outlined" />
                  <Chip size="small" label={`周期 ${tier.monthly_period}`} variant="outlined" />
                </Box>
              </Box>
            )}

            <Divider />

            <FormControl fullWidth>
              <InputLabel>目标等级</InputLabel>
              <Select
                value={targetCode}
                label="目标等级"
                onChange={(e) => setTargetCode(e.target.value)}
              >
                {allLevels.map((lv) => (
                  <MenuItem key={lv.code} value={lv.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{lv.tier_icon}</span>
                      <span style={{ flex: 1 }}>{lv.tier_name} · {lv.label}</span>
                      <Chip size="small" label={`code=${lv.code}`} sx={{ height: 18, fontSize: 11 }} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ p: 1.5, border: `1px dashed ${targetTier.color}`, borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">调整后</Typography>
              <Typography variant="h6" sx={{ color: targetTier.color, fontWeight: 700 }}>
                {targetTier.icon} {targetTier.name} · {codeToLabel(parseInt(targetCode, 10))}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="调整原因（可选）"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="例：客服补偿、活动奖励、错误修正..."
              multiline
              rows={2}
            />

            <Alert severity="info" variant="outlined" sx={{ fontSize: 13 }}>
              该操作会写入 tier_settlements 流水（reason=admin_manual_adjust），不影响月度积分。
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>取消</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || (tier && parseInt(targetCode, 10) === tier.current_code)}
        >
          {submitting ? <CircularProgress size={18} /> : '确认调整'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TierAdjustDialog;
